/**
 * ============================================================
 * API UTILITY FUNCTIONS
 * ============================================================
 * ✅ UPDATED: Now uses shared api client with interceptors
 * ✅ AUTH: Token auto-attached via request interceptor
 * ✅ 401: Auto-logout via response interceptor
 *
 * ⚠️ IMPORTANT: Always use 'api' from './api/client'
 * ⚠️ NEVER use raw axios or fetch for authenticated requests
 * ============================================================
 */

import api from "../api/client";
import { API_URL } from "../apiConfig";
import {
  getUnityDualSiteLocationIds,
  resolveClientFetchParams,
  shouldUseUnityCompanyClientScope,
} from "./unityLocationScope";

// ============================================================
// LOCATIONS API
// ============================================================

export const fetchLocations = async () => {
  const response = await api.get("/locations/my-locations");
  return response.data;
};

export const createLocation = async (location) => {
  const response = await api.post("/locations", location);
  return response.data;
};

export const updateLocation = async (id, location) => {
  const response = await api.put(`/locations/${id}`, location);
  return response.data;
};

export const deleteLocation = async (id) => {
  const response = await api.delete(`/locations/${id}`);
  return response.data;
};

export const setActiveLocation = async (id) => {
  const response = await api.put(`/locations/${id}/set-active`);
  return response.data;
};

// ============================================================
// USERS API
// ============================================================

export const fetchUsers = async (params = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== "",
    ),
  ).toString();
  const url = query ? `/users?${query}` : "/users";
  const response = await api.get(url);
  return response.data;
};

export const createUser = async (userData) => {
  const response = await api.post("/users", userData);
  return response.data;
};

export const updateUser = async (id, userData) => {
  const response = await api.put(`/users/${id}`, userData);
  return response.data;
};

export const acceptTermsOfUse = async ({ termsVersion } = {}) => {
  const response = await api.post("/auth/accept-terms", {
    termsVersion: termsVersion || null,
  });
  return response.data;
};

export const deleteUser = async (id) => {
  const response = await api.delete(`/users/${id}`);
  return response.data;
};

// ============================================================
// FOLDERS API
// ============================================================

export const fetchClientFolders = async (locationId) => {
  const url = locationId ? `/folders?locationId=${locationId}` : "/folders";
  const response = await api.get(url);
  return response.data;
};

/** Folders for active location, or merged Flemington + Princeton when Unity dual-site. */
export const fetchClientFoldersForScope = async (activeLocation, locations = []) => {
  const { locationIds, scope } = resolveClientFetchParams(activeLocation, locations);
  if (scope !== "unity-company" || locationIds.length <= 1) {
    return fetchClientFolders(locationIds[0] || null);
  }
  const batches = await Promise.all(
    locationIds.map((id) => fetchClientFolders(id).catch(() => [])),
  );
  const byId = new Map();
  batches.flat().forEach((folder) => {
    const fid = folder?.id || folder?._id;
    if (fid) byId.set(String(fid), folder);
  });
  return [...byId.values()];
};

/** Clients for active location, or full Unity company when on Flemington / Princeton. */
export const fetchClientsForScope = async (
  activeLocation,
  locations = [],
  options = {},
) => {
  const params = resolveClientFetchParams(activeLocation, locations);
  const q = options.q || options.searchTerm;
  if (params.scope === "unity-company" && params.companyId) {
    return fetchClients({ companyId: params.companyId, q });
  }
  return fetchClients({
    companyId: params.companyId,
    locationId: params.locationId,
    q,
  });
};

export { shouldUseUnityCompanyClientScope, getUnityDualSiteLocationIds };

export const fetchFolder = async (folderId) => {
  const response = await api.get(`/folders/${folderId}`);
  return response.data;
};

export const createClientFolder = async (folderData) => {
  const response = await api.post("/folders", folderData);
  return response.data;
};

export const updateClientFolder = async (folderId, folderData) => {
  const response = await api.put(`/folders/${folderId}`, folderData);
  return response.data;
};

export const deleteClientFolder = async (folderId) => {
  const response = await api.delete(`/folders/${folderId}`);
  return response.data;
};

// ============================================================
// CLIENTS API
// ============================================================

export const fetchClients = async (
  locationIdOrOptions,
  searchTerm = "",
  companyId = null,
) => {
  const params = {};

  // Flexible signature:
  // - fetchClients({ locationId, companyId, searchTerm })
  // - fetchClients(locationId, searchTerm?, companyId?)
  // - fetchClients({ companyId })
  if (
    typeof locationIdOrOptions === "object" &&
    locationIdOrOptions !== null &&
    !Array.isArray(locationIdOrOptions)
  ) {
    const opts = locationIdOrOptions;
    if (opts.locationId) params.locationId = opts.locationId;
    if (opts.folderId) params.folderId = opts.folderId;
    if (opts.companyId) params.companyId = opts.companyId;
    const q = opts.searchTerm || opts.q;
    if (q) params.q = q.trim();
  } else {
    if (locationIdOrOptions) params.locationId = locationIdOrOptions;
    if (companyId) params.companyId = companyId;
    if (searchTerm) params.q = searchTerm.trim();
  }

  // Prevent stale cached client lists
  params._ts = Date.now();

  const query = new URLSearchParams(params).toString();
  const url = query ? `/clients?${query}` : "/clients";
  const response = await api.get(url);
  return response.data;
};

export const fetchClient = async (clientId) => {
  const response = await api.get(`/clients/${clientId}`);
  return response.data;
};

export const getCanarySubject = async (clientId) => {
  const response = await api.get(`/clients/${clientId}/canary-subject`);
  return response.data;
};

export const createClient = async (clientData) => {
  const response = await api.post("/clients", clientData);
  return response.data;
};

export const updateClient = async (clientId, clientData) => {
  // Use longer timeout when payload includes avatar (base64 can be large)
  const hasAvatar = Boolean(clientData?.avatarUrl && String(clientData.avatarUrl).startsWith("data:"));
  const config = hasAvatar ? { timeout: 60000 } : {};
  const response = await api.put(`/clients/${clientId}`, clientData, config);
  return response.data;
};

export const deleteClient = async (clientId) => {
  const response = await api.delete(`/clients/${clientId}`);
  return response.data;
};

// Bulk caregiver activity (visit count + distinct caregiver count per
// 7/30/90/180/360-day window) for every client in a folder/location.
// One call per folder/location view — never call this per-row.
export const fetchCaregiverActivity = async ({ folderId, locationId, clientIds } = {}) => {
  const params = {};
  if (folderId) params.folderId = folderId;
  else if (locationId) params.locationId = locationId;
  else if (clientIds?.length) params.clientIds = clientIds.join(",");
  else return {};

  const query = new URLSearchParams(params).toString();
  const response = await api.get(`/clients/caregiver-activity?${query}`);
  return response.data?.activity || {};
};

// Detail view for a single client's "Care Count" section: per-window stats,
// 90-day caregiver breakdown, and a 13-week visit series for the chart.
export const fetchCaregiverActivityDetail = async (clientId, { refresh = false } = {}) => {
  if (!clientId) return null;
  const qs = refresh ? "?refresh=1" : "";
  const response = await api.get(
    `/clients/${clientId}/caregiver-activity-detail${qs}`,
  );
  return response.data;
};

// CareLink360 activity summary for a client's "CareLink 360" snapshot section:
// a per-report-type rollup (activity, calls, pictures, schedule, videos) for the
// CareLink360 person this client is linked to. Returns { linked: false } when
// the client has no CareLink360 counterpart.
export const fetchCareLink360Summary = async (clientId) => {
  if (!clientId) return null;
  const response = await api.get(`/clients/${clientId}/carelink360-summary`);
  return response.data;
};

// ============================================================
// MEDWAND
// ============================================================

// Exams captured on a MedWand device for one client, newest first, plus the
// latest value per sensor. Unlike the polled integrations there is nothing to
// trigger from here — MedWand pushes each exam to our callback as it completes,
// so the panel only ever reads.
//
// Resolves to { linked, totalExams, lastExamAt, latestReadings, upcoming,
// exams } — or { linked: false } when the client has never had an appointment.
export const fetchMedWandSummary = async (clientId) => {
  if (!clientId) return null;
  const response = await api.get(`/medwand/clients/${clientId}/summary`);
  return response.data;
};

// Ask CareLink360 for fresh data behind this client's snapshot. CareLink360
// serves whole reports only, so this queues a pull for the client's agency and
// is throttled agency-wide — a 429 means someone synced it moments ago.
// Resolves to { status, lastSyncAt, inProgress, cooldownSeconds, ... }.
export const triggerCareLink360Sync = async (clientId) => {
  if (!clientId) return null;
  const response = await api.post(`/clients/${clientId}/carelink360-sync`);
  return response.data;
};

// PacSana panel for a client's Snapshot page: the bracelet this client is
// linked to, its current state (status, battery, location), and daily sleep /
// active-minutes / exercise / community / gait-speed series for the charts.
// Returns { linked: false } when no PacSana user is linked to the client.
//
// `range` is an optional { from, to } pair of yyyy-MM-dd dates, sent on as
// dateFrom/dateTo — PacSana's historical endpoint answers at most 31 days per
// call. Omitted entirely when no range is asked for, so the default window the
// backend picks is left alone.
export const fetchPacSanaSummary = async (clientId, range = null, events = null) => {
  if (!clientId) return null;
  const params = {};
  if (range?.from && range?.to) {
    params.dateFrom = range.from;
    params.dateTo = range.to;
  }
  // PacSana pages the event list on this same call, so the page travels with it.
  if (events?.page) params.eventsPage = events.page;
  if (events?.pageSize) params.eventsPageSize = events.pageSize;

  const response = await api.get(`/clients/${clientId}/pacsana-summary`, {
    params: Object.keys(params).length ? params : undefined,
  });
  return response.data;
};

// One day of the PacSana 24-hour activity clock. PacSana serves this a day at a
// time, so the panel asks per day as the picker moves rather than the summary
// carrying a month of them. Resolves to { date, activity, location, source }.
export const fetchPacSanaDayActivity = async (clientId, date) => {
  if (!clientId || !date) return null;
  const response = await api.get(`/clients/${clientId}/pacsana-day-activity`, {
    params: { date },
  });
  return response.data;
};

// Ask PacSana for fresh readings behind this client's snapshot. PacSana has no
// per-user pull, so this queues a roster-wide pull for the client's agency and
// is throttled agency-wide — a 429 means someone synced it moments ago.
// Resolves to { status, lastSyncAt, inProgress, cooldownSeconds, ... }.
export const triggerPacSanaSync = async (clientId) => {
  if (!clientId) return null;
  const response = await api.post(`/clients/${clientId}/pacsana-sync`);
  return response.data;
};

// ============================================================
// SENSI REPORT
// ============================================================

// Upload one of a client's Sensi PDF reports. The backend reads the report into
// JSON and discards the PDF itself — Albert never stores the file. One file per
// request so each row in the upload list gets its own progress and outcome.
//
// `onProgress` receives 0–100 as bytes go out; reaching 100 means the server is
// now extracting the report. Resolves to { id, fileName, pageCount, uploadedAt,
// status, error }.
export const uploadSensiReport = async (clientId, file, { onProgress } = {}) => {
  const form = new FormData();
  form.append("file", file);

  const response = await api.post(
    `/integrations/sensi-report/clients/${clientId}/uploads`,
    form,
    {
      // Extraction runs inside the request, so allow longer than the client default.
      timeout: 120000,
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        onProgress(Math.round((event.loaded / event.total) * 100));
      },
    },
  );
  return response.data;
};

// A client's earlier Sensi report uploads, newest first. These are the standing
// record the upload list shows beneath whatever this visit has added.
// Resolves to { uploads: [{ id, fileName, pageCount, uploadedAt, status, error }], total }.
export const fetchSensiReportUploads = async (clientId, { page, limit } = {}) => {
  if (!clientId) return null;
  const response = await api.get(
    `/integrations/sensi-report/clients/${clientId}/uploads`,
    { params: { page, limit } },
  );
  return response.data;
};

// One stored Sensi report in full, for the panel's preview: the parsed report
// under `extraction`. The PDF was discarded at upload, so this is what Albert
// has of it.
export const fetchSensiReport = async (clientId, uploadId) => {
  const response = await api.get(
    `/integrations/sensi-report/clients/${clientId}/uploads/${uploadId}`,
  );
  return response.data;
};

// Removes one of a client's Sensi reports from the upload list. The server
// keeps the extracted report and only hides it, so this is reversible there,
// and the same PDF may be uploaded again afterwards.
export const deleteSensiReportUpload = async (clientId, uploadId) => {
  const response = await api.delete(
    `/integrations/sensi-report/clients/${clientId}/uploads/${uploadId}`,
  );
  return response.data;
};

export const calculateClientScores = async (clientId, analysisData) => {
  const response = await api.post(
    `/clients/${clientId}/calculate-scores`,
    analysisData,
  );
  return response.data;
};

export const fetchAipHistory = async (clientId, range = "last3m") => {
  const response = await api.get(`/aip-history/${clientId}`, {
    params: { range },
  });
  return response.data;
};

export const createAipHistory = async (payload) => {
  const response = await api.post(`/aip-history`, payload);
  return response.data;
};

/**
 * Fetch org-wide AIP score changes for the AIP Change Center dashboard widget.
 * @param {string} locationId  - active location id (required)
 * @param {string} range       - "today" | "week" | "month"  (default: "today")
 * @param {number} limit       - max rows to return (default: 5)
 * @returns {{ range, results, totalMatching }}
 */
export const AIP_CHANGE_VIEW_ALL_LIMIT = 100;

export const fetchAipChanges = async (locationId, range = "today", limit = 5) => {
  const response = await api.get("/aip-history/changes", {
    params: { locationId, range, limit },
  });
  return response.data;
};

/**
 * Fetch every client in location scope for the AIP Change Center View All page.
 * Range affects Previous/Change comparison only — all clients are always returned.
 */
export const fetchAipChangesAll = async (locationId, range = "today") => {
  const response = await api.get("/aip-history/changes", {
    params: { locationId, range, view: "all" },
  });
  return response.data;
};

/** Unity Flemington + Princeton (or single location) — one roster for Care Hub / Change Center. */
export const fetchAipChangesAllForScope = async (
  activeLocation,
  locations = [],
  range = "today",
) => {
  const params = resolveClientFetchParams(activeLocation, locations);
  if (params.scope === "unity-company" && params.companyId) {
    const locationIds = (params.locationIds || []).filter(Boolean).join(",");
    const response = await api.get("/aip-history/changes", {
      params: {
        companyId: params.companyId,
        ...(locationIds ? { locationIds } : {}),
        range,
        view: "all",
      },
    });
    return response.data;
  }
  return fetchAipChangesAll(params.locationId, range);
};

export const fetchAipChangesForScope = async (
  activeLocation,
  locations = [],
  range = "today",
  limit = 5,
) => {
  const params = resolveClientFetchParams(activeLocation, locations);
  if (params.scope === "unity-company" && params.companyId) {
    const locationIds = (params.locationIds || []).filter(Boolean).join(",");
    const response = await api.get("/aip-history/changes", {
      params: {
        companyId: params.companyId,
        ...(locationIds ? { locationIds } : {}),
        range,
        limit,
      },
    });
    return response.data;
  }
  return fetchAipChanges(params.locationId, range, limit);
};

export const fetchCareTriageWorkflows = async (locationId) => {
  const response = await api.get("/care-triage/workflows", {
    params: { locationId },
  });
  return response.data?.workflows || [];
};

export const fetchCareTriageAssignees = async (locationId) => {
  const response = await api.get("/care-triage/assignees", {
    params: { locationId },
  });
  return {
    groups: response.data?.groups || [],
    assignees: response.data?.assignees || [],
  };
};

export const assignCareTriageCase = async (payload) => {
  const response = await api.post("/care-triage/workflows/assign", payload);
  return response.data?.workflow;
};

export const startCareTriageCase = async (eventKey, locationId) => {
  const response = await api.post(`/care-triage/workflows/${encodeURIComponent(eventKey)}/start`, {
    locationId,
  });
  return response.data?.workflow;
};

export const postCareTriageMessage = async (eventKey, payload) => {
  const response = await api.post(
    `/care-triage/workflows/${encodeURIComponent(eventKey)}/message`,
    payload,
  );
  return response.data?.workflow;
};

export const closeCareTriageCase = async (eventKey, payload) => {
  const response = await api.post(
    `/care-triage/workflows/${encodeURIComponent(eventKey)}/close`,
    payload,
  );
  return response.data?.workflow;
};

export const reopenCareTriageCase = async (eventKey, payload) => {
  const response = await api.post(
    `/care-triage/workflows/${encodeURIComponent(eventKey)}/reopen`,
    payload,
  );
  return response.data?.workflow;
};

export const markCareTriageRead = async (eventKey, payload) => {
  const response = await api.post(
    `/care-triage/workflows/${encodeURIComponent(eventKey)}/mark-read`,
    payload,
  );
  return response.data?.workflow;
};

// ============================================================
// AI HIRING API (Agency)
// ============================================================

export const fetchAiHiringOverview = async (locationId, days) => {
  const response = await api.get("/ai-hiring/overview", {
    params: { locationId, days },
  });
  return response.data;
};

export const fetchAiHiringCandidates = async ({
  locationId,
  status = "all",
  source = "all",
  search = "",
  page = 1,
  limit = 50,
  jobId = "",
  sortBy,
  order,
} = {}) => {
  const response = await api.get("/ai-hiring/candidates", {
    params: {
      locationId,
      status,
      source,
      search,
      page,
      limit,
      jobId,
      ...(sortBy ? { sortBy } : {}),
      ...(order ? { order } : {}),
    },
  });
  return response.data;
};

export const unlockMoreAiHiringCandidates = async ({
  locationId,
  jobId,
  reason,
} = {}) => {
  const response = await api.post(
    "/ai-hiring/candidates/show-more",
    { jobId, reason },
    { params: { locationId } },
  );
  return response.data;
};

export const getCandidateRecording = async (candidateId, locationId) => {
  const response = await api.get(`/ai-hiring/recording/${candidateId}`, {
    params: { locationId },
  });
  return response.data;
};

export const getCandidatePhoneRecording = async (candidateId, locationId) => {
  const response = await api.get(`/ai-hiring/phone-recording/${candidateId}`, {
    params: { locationId },
  });
  return response.data;
};

export const fetchAiHiringSettings = async (locationId) => {
  const response = await api.get("/ai-hiring/settings", {
    params: { locationId },
  });
  return response.data;
};

export const updateAiHiringSettings = async (locationId, payload) => {
  const response = await api.put("/ai-hiring/settings", payload, {
    params: { locationId },
  });
  return response.data;
};

// LiveAvatar Agent Selection (2026-07-28) — real name/preview_url for each
// configured LiveAvatar avatar option, straight from LiveAvatar's own
// Avatar API (see routes/liveavatar.js's GET /agent-options), so the
// settings page can show an actual picture before an agency user picks one.
export const fetchLiveAvatarAgentOptions = async () => {
  const response = await api.get("/liveavatar/agent-options");
  return response.data;
};

export const createAiHiringPlanCheckoutSession = async ({
  planType,
  locationId,
} = {}) => {
  const response = await api.post("/ai-hiring/create-checkout-session", {
    planType: String(planType || "").trim(),
    ...(locationId ? { locationId: String(locationId).trim() } : {}),
  });
  return response.data;
};

// Combined AI Hiring plan (Member/Non-Member, admin-set tier) + Platform
// Access + Data Usage — one bundled Checkout Session. planType is NEVER
// sent from here — the backend always re-derives it server-side from
// Agency.aiHiringPlan (Super-Admin set), never a client-supplied value.
export const createAiHiringFullBundleCheckoutSession = async ({
  locationId,
} = {}) => {
  const response = await api.post(
    "/ai-hiring/checkout/full-bundle",
    locationId ? { locationId: String(locationId).trim() } : {},
  );
  return response.data;
};

export const fetchAiHiringPlanStatus = async (locationIdOrOptions) => {
  const locationId =
    typeof locationIdOrOptions === "object" && locationIdOrOptions !== null
      ? locationIdOrOptions.locationId
      : locationIdOrOptions;
  try {
    const response = await api.get("/ai-hiring/plan-status", {
      params: locationId ? { locationId } : undefined,
    });
    return response.data;
  } catch (err) {
    // Treat missing agency mapping as "plan not provisioned yet"
    // so AI Hiring UI stays in locked/inactive mode without hard failure.
    if (err?.response?.status === 404) {
      return {
        planActive: false,
        planType: null,
        purchasedAt: null,
        notProvisioned: true,
      };
    }
    throw err;
  }
};

// ── AI Call Assist billing (Unity-Princeton pilot) ──
// Mirrors the AI Hiring plan helpers above; backend resolves agencyId
// server-side from locationId, never trusts a client-supplied agencyId.
export const fetchAiCallAssistStatus = async ({ locationId } = {}) => {
  try {
    const response = await api.get("/ai-call-assist/status", {
      params: locationId ? { locationId } : undefined,
    });
    return response.data;
  } catch (err) {
    if (err?.response?.status === 404) {
      return {
        paymentEnabled: false,
        platformAccess: { purchased: false, purchasedAt: null },
        dataUsage: { active: false, activatedAt: null, canceledAt: null },
        aiCallAssist: { active: false, activatedAt: null, canceledAt: null },
        notProvisioned: true,
      };
    }
    throw err;
  }
};

export const createAiCallAssistCheckoutSession = async ({
  product,
  locationId,
} = {}) => {
  const response = await api.post(
    `/ai-call-assist/checkout/${String(product || "").trim()}`,
    locationId ? { locationId: String(locationId).trim() } : {},
  );
  return response.data;
};

export const cancelAiCallAssistSubscription = async ({
  product,
  locationId,
} = {}) => {
  const response = await api.post(
    `/ai-call-assist/cancel/${String(product || "").trim()}`,
    locationId ? { locationId: String(locationId).trim() } : {},
  );
  return response.data;
};

// ── Agent247 (24/7 AI Call Assist) Member/Non-Member billing ──
// Separate flow from the AI Call Assist product above (Platform Access/Data
// Usage/AI Call Assist) — different Agency fields, different Stripe prices.
// agencyId is always resolved server-side from locationId, never sent by the
// client; pricing tier (member/non_member) is likewise always re-derived
// server-side from Agency.agent247MembershipStatus, never sent by the client.
export const fetchAgent247BillingStatus = async ({ locationId } = {}) => {
  try {
    const response = await api.get("/agent247/billing/status", {
      params: locationId ? { locationId } : undefined,
      // Belt-and-suspenders: also send the active-location header (same
      // convention as activateTestPlan below) — resolveAgencyFromRequest
      // checks this header before the query param, so this guarantees the
      // request always carries the switcher's current selection even if a
      // param ever gets stripped/dropped along the way.
      headers: locationId
        ? { "x-active-location-id": String(locationId).trim() }
        : undefined,
    });
    return response.data;
  } catch (err) {
    if (err?.response?.status === 404) {
      return {
        agent247MembershipStatus: "non_member",
        plan: null,
        status: "pending",
        setupFeeInvoiced: false,
        stripeSubscriptionId: null,
        billingInterval: "monthly",
        pricing: {
          callAssistAmount: null,
          callAssistInterval: null,
          callAssistAnnualAmount: null,
          callAssistAnnualInterval: null,
          setupFeeAmount: null,
          platformFeeAmount: null,
        },
        pilotEnabled: false,
        notProvisioned: true,
      };
    }
    throw err;
  }
};

export const createAgent247BillingCheckoutSession = async ({
  locationId,
  billingInterval,
} = {}) => {
  const body = {};
  if (locationId) body.locationId = String(locationId).trim();
  if (billingInterval) body.billingInterval = String(billingInterval).trim();
  const response = await api.post(
    "/agent247/billing/create-checkout-session",
    body,
    {
      // Same belt-and-suspenders header as fetchAgent247BillingStatus above.
      headers: locationId
        ? { "x-active-location-id": String(locationId).trim() }
        : undefined,
    },
  );
  return response.data;
};

// ── Billing Information / Payment History (shared across all billing products) ──
export const fetchBillingInfo = async ({ locationId } = {}) => {
  const response = await api.get("/billing/info", {
    params: locationId ? { locationId } : undefined,
  });
  return response.data;
};

export const fetchBillingHistory = async ({ locationId, page, limit } = {}) => {
  const response = await api.get("/billing/history", {
    params: {
      ...(locationId ? { locationId } : {}),
      ...(page ? { page } : {}),
      ...(limit ? { limit } : {}),
    },
  });
  return response.data;
};

export const createBillingPortalSession = async ({ locationId } = {}) => {
  const response = await api.post(
    "/billing/portal-session",
    locationId ? { locationId: String(locationId).trim() } : {},
  );
  return response.data;
};

export const activateTestPlan = async ({ planType, locationId } = {}) => {
  const response = await api.post(
    "/ai-hiring/activate-test-plan",
    { planType: String(planType || "").trim() },
    {
      headers: {
        "x-test-secret": "albert-test-2026",
        ...(locationId
          ? { "x-active-location-id": String(locationId).trim() }
          : {}),
      },
    },
  );
  return response.data;
};

export const purchaseAiHiringPackage = async ({
  locationId,
  level,
} = {}) => {
  const response = await api.post(
    "/ai-hiring/purchase",
    { level },
    { params: { locationId } },
  );
  return response.data;
};

export const finalizeAiHiringPurchase = async ({
  agencyId,
  level,
  hiringCredits,
} = {}) => {
  const response = await api.post("/ai-hiring/stripe/checkout-complete", {
    eventType: "checkout.session.completed",
    agencyId,
    level,
    hiringCredits,
  });
  return response.data;
};

// Active location headers for Agent247 scoping (backend resolves organizationId
// from x-active-location-id / x-active-company-id when the param is missing).
const getActiveLocationScopeHeaders = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("activeLocation") || "null");
    if (!stored || typeof stored !== "object") return undefined;
    const locationId = stored.id || stored._id;
    const companyId =
      stored.companyId?._id ||
      stored.companyId?.id ||
      stored.companyId;
    const headers = {};
    if (locationId) headers["x-active-location-id"] = String(locationId).trim();
    if (companyId) headers["x-active-company-id"] = String(companyId).trim();
    return Object.keys(headers).length ? headers : undefined;
  } catch {
    return undefined;
  }
};

export const fetchAgent247Config = async (organizationId) => {
  const response = await api.get("/agent247/config", {
    params: { organizationId },
    headers: getActiveLocationScopeHeaders(),
  });
  return response.data;
};

export const updateAgent247Config = async (organizationId, payload) => {
  const response = await api.put("/agent247/config", {
    organizationId,
    ...payload,
  }, {
    headers: getActiveLocationScopeHeaders(),
  });
  return response.data;
};

export const updateAgent247NotificationRecipients = async (organizationId, payload) => {
  const response = await api.patch("/agent247/notification-recipients", {
    organizationId,
    ...payload,
  }, {
    headers: getActiveLocationScopeHeaders(),
  });
  return response.data;
};

export const testAgent247Live = async (organizationId, payload = {}) => {
  const response = await api.post("/agent247/test-agent-live", {
    organizationId,
    ...payload,
  }, {
    headers: getActiveLocationScopeHeaders(),
  });
  return response.data;
};

export const fetchAgent247Events = async (organizationId, limit = 100, q = "") => {
  const response = await api.get("/agent247/events", {
    params: { organizationId, limit, ...(q ? { q } : {}) },
    headers: getActiveLocationScopeHeaders(),
  });
  return response.data;
};

// Lazy full-detail fetch (2026-07-24 over-fetching fix): the list response above no
// longer includes the full transcript/raw payload for every row (see routes/agent247.js
// EVENTS_LIST_SELECT) — this fetches the complete event, including that full payload,
// for exactly the one event a user opens in the detail panel.
export const fetchAgent247EventDetail = async (organizationId, eventId) => {
  const response = await api.get(`/agent247/events/${eventId}`, {
    params: { organizationId },
  });
  return response.data;
};

export const recordAgent247EventSeenApi = async ({ organizationId, eventId }) => {
  const response = await api.post("/agent247/events/seen", {
    organizationId: String(organizationId || "").trim(),
    eventId: String(eventId || "").trim(),
  });
  return response.data;
};

export const deleteAgent247Event = async (organizationId, eventId) => {
  const response = await api.delete(`/agent247/events/${eventId}`, {
    params: { organizationId },
  });
  return response.data;
};

export const patchAgent247EventFollowUpStatus = async (
  organizationId,
  eventId,
  followUpStatus,
) => {
  const response = await api.patch(
    `/agent247/events/${eventId}`,
    { followUpStatus },
    { params: { organizationId } },
  );
  return response.data;
};

// ============================================================
// SUPPORT TICKETS (MyAlbertAI tech support)
// ============================================================

export const fetchSupportTickets = async (params = {}) => {
  const response = await api.get("/support-tickets", { params });
  return response.data;
};

export const fetchSupportTicket = async (ticketId) => {
  const response = await api.get(`/support-tickets/${ticketId}`);
  return response.data;
};

export const createSupportTicket = async ({ subject, description, screenshots = [] }) => {
  const formData = new FormData();
  formData.append("subject", subject);
  formData.append("description", description);
  screenshots.forEach((file) => formData.append("screenshots", file));

  const token = localStorage.getItem("token");
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    const stored = JSON.parse(localStorage.getItem("activeLocation") || "null");
    const locationId = stored?.id || stored?._id;
    if (locationId) headers["x-active-location-id"] = String(locationId);
  } catch {
    // ignore
  }

  const response = await fetch(`${API_URL}/support-tickets`, {
    method: "POST",
    headers,
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data?.message || "Failed to submit support ticket");
    err.response = { data };
    throw err;
  }
  return data;
};

export const addSupportTicketComment = async (ticketId, text) => {
  const response = await api.post(`/support-tickets/${ticketId}/comments`, { text });
  return response.data;
};

export const getSupportTicketAttachmentUrl = (filename) =>
  `${API_URL}/support-tickets/attachments/${encodeURIComponent(filename)}`;

export const fetchSupportTicketOpenCount = async () => {
  const response = await api.get("/support-tickets/open-count");
  return response.data;
};

// ============================================================
// JOBS API (AI Hiring - Agency Job Roles)
// ============================================================

export const fetchAgencyJobs = async (locationId) => {
  const response = await api.get("/agency/jobs", { params: { locationId } });
  return response.data;
};

export const fetchAgencyJobDistributionStatus = async (locationId, jobId) => {
  const response = await api.get(`/agency/jobs/${jobId}/distribution-status`, {
    params: { locationId },
  });
  return response.data;
};

export const createAgencyJob = async (locationId, payload) => {
  const response = await api.post("/agency/jobs", payload, {
    params: { locationId },
  });
  return response.data;
};

export const updateAgencyJob = async (locationId, jobId, payload) => {
  const response = await api.put(`/agency/jobs/${jobId}`, payload, {
    params: { locationId },
  });
  return response.data;
};

export const deleteAgencyJob = async (locationId, jobId) => {
  const response = await api.delete(`/agency/jobs/${jobId}`, {
    params: { locationId },
  });
  return response.data;
};

export const invalidateIndeedFeedCache = async (locationId) => {
  const response = await api.post("/job-board/feed-cache/invalidate", null, {
    params: { locationId },
  });
  return response.data;
};

export const fetchAgencyBoardSettings = async (agencyId) => {
  const response = await api.get(`/agencies/${agencyId}/board-settings`);
  return response.data;
};

export const saveAgencyBoardSettings = async (agencyId, payload) => {
  const response = await api.post(`/agencies/${agencyId}/board-settings`, payload);
  return response.data;
};

export const disconnectAgencyBoardProvider = async (agencyId, provider) => {
  const response = await api.delete(`/agencies/${agencyId}/board-settings/${provider}`);
  return response.data;
};

export const fetchAgencyPublicLinks = async (locationId) => {
  const response = await api.get("/agency/public-links", {
    params: { locationId },
  });
  return response.data;
};

export const updateAiHiringDecision = async (
  locationId,
  candidateId,
  decision,
  reason,
) => {
  const response = await api.post(
    `/ai-hiring/candidates/${candidateId}/decision`,
    { decision, reason },
    { params: { locationId } },
  );
  return response.data;
};

export const fetchAiHiringDripCampaigns = async ({ locationId } = {}) => {
  const response = await api.get("/ai-hiring/drip-campaigns", {
    params: { locationId },
  });
  return response.data;
};

export const stopAiHiringDripCampaign = async ({ candidateId } = {}) => {
  const response = await api.post("/drip/stop", { candidateId });
  return response.data;
};

// ============================================================
// VISIT NOTES API
// ============================================================

export const fetchVisitNotes = async (clientId) => {
  const response = await api.get(`/visit-notes/client/${clientId}`);
  return response.data;
};

export const fetchShiftVoiceCalls = async (clientId) => {
  const response = await api.get(`/visit-notes/client/${clientId}/voice-calls`);
  return response.data;
};

// Shared fetch limit for Agent Albert alerts. Both Dashboard.js and
// AlbertAiAlertsPage.js must import and use this constant so they always
// request the same candidate set — passing different limits was the secondary
// cause of the badge-vs-page unread-count discrepancy (fixed July 2026).
export const AGENT_ALERTS_FETCH_LIMIT = 200;

// Dashboard.js and Sidebar.jsx (via useShiftReportUnreadCount) each independently poll
// this same endpoint for the same location on the same 60s cadence, so every page load
// (and every subsequent poll tick) fires two identical concurrent requests — doubling
// backend load on an endpoint that's already expensive per-request. This is not a race
// (both are pure reads, no shared/mutable state), but it is pure duplication: coalesce
// concurrent calls for the same locationId+limit into a single in-flight request so both
// callers share one round trip instead of firing two (confirmed root cause, 2026-07-24).
const inFlightAgentAlbertAlertsRequests = new Map();

export const fetchAiKathyCalls = async (locationId, limit = 50) => {
  const response = await api.get("/ai-kathy/calls", {
    params: { locationId: String(locationId || ""), limit },
  });
  return response.data;
};

export const fetchAiKathyCallDetail = async (callId) => {
  const response = await api.get(`/ai-kathy/calls/${callId}`);
  return response.data;
};

export const updateAiKathyCall = async (callId, payload) => {
  const response = await api.patch(`/ai-kathy/calls/${callId}`, payload);
  return response.data;
};

export const deleteAiKathyCall = async (callId) => {
  const response = await api.delete(`/ai-kathy/calls/${callId}`);
  return response.data;
};

export const fetchAgentAlbertAlerts = async (locationId, limit = AGENT_ALERTS_FETCH_LIMIT) => {
  const key = `${String(locationId || "")}:${limit}`;
  const existing = inFlightAgentAlbertAlertsRequests.get(key);
  if (existing) return existing;

  const requestPromise = (async () => {
    const response = await api.get("/visit-notes/agent-alerts", {
      params: { locationId, limit },
    });
    return response.data;
  })();

  inFlightAgentAlbertAlertsRequests.set(key, requestPromise);
  try {
    return await requestPromise;
  } finally {
    inFlightAgentAlbertAlertsRequests.delete(key);
  }
};

export const recordAgentAlbertAlertSeenApi = async ({ locationId, alertId }) => {
  const response = await api.post("/visit-notes/agent-alerts/seen", {
    locationId: String(locationId || "").trim(),
    alertId: String(alertId || "").trim(),
  });
  return response.data;
};

export const patchAgentAlbertAlertFollowUpStatus = async (
  locationId,
  alertId,
  followUpStatus,
) => {
  const response = await api.patch("/visit-notes/agent-alerts/status", {
    locationId: String(locationId || "").trim(),
    alertId: String(alertId || "").trim(),
    followUpStatus: String(followUpStatus || "").trim(),
  });
  return response.data;
};

export const fetchAgentAlbertAlertNote = async ({
  clientId,
  voiceCallId,
  visitNoteId,
}) => {
  const params = { clientId: String(clientId || "").trim() };
  if (voiceCallId) params.voiceCallId = String(voiceCallId);
  if (visitNoteId) params.visitNoteId = String(visitNoteId);
  const response = await api.get("/visit-notes/agent-alert-note", { params });
  return response.data;
};

export const fetchAgentAlbertAlertShift = async ({
  clientId,
  appointmentId,
  stage,
}) => {
  const params = {
    clientId: String(clientId || "").trim(),
    appointmentId: String(appointmentId || "").trim(),
  };
  if (stage) params.stage = String(stage);
  const response = await api.get("/visit-notes/agent-alert-shift", { params });
  return response.data;
};

export const regenerateShiftVoiceCallSummary = async (clientId, callId) => {
  const response = await api.post(
    `/visit-notes/client/${clientId}/voice-calls/${callId}/regenerate-summary`,
  );
  return response.data;
};

export const fetchLatestVisitNote = async (clientId) => {
  const notes = await fetchVisitNotes(clientId);
  // Return the most recent note (sorted by date)
  if (notes && notes.length > 0) {
    return notes[notes.length - 1]; // Assuming sorted by date ascending
  }
  return null;
};

export const createVisitNote = async (visitNoteData) => {
  const response = await api.post("/visit-notes", visitNoteData);
  return response.data;
};

export const updateVisitNote = async (id, visitNoteData) => {
  const response = await api.put(`/visit-notes/${id}`, visitNoteData);
  return response.data;
};

export const deleteVisitNote = async (id) => {
  const response = await api.delete(`/visit-notes/${id}`);
  return response.data;
};

export const getVisitNoteFeedback = async (observations) => {
  const response = await api.post("/visit-notes/ai-feedback", { observations });
  return response.data;
};

// Send AIP report via email
export const sendAipReport = async (payload) => {
  const response = await api.post("/admin/send-aip-report", payload);
  return response.data;
};

// ============================================================
// QUESTION SETS API
// ============================================================

export const fetchQuestionSets = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.type) queryParams.append("type", params.type);
  const url = `/question-sets${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;
  const response = await api.get(url);
  return response.data;
};

export const fetchQuestionSet = async (id) => {
  const response = await api.get(`/question-sets/${id}`);
  return response.data;
};

export const createQuestionSet = async (questionSetData) => {
  const response = await api.post("/question-sets", questionSetData);
  return response.data;
};

export const updateQuestionSet = async (id, questionSetData) => {
  const response = await api.put(`/question-sets/${id}`, questionSetData);
  return response.data;
};

export const deleteQuestionSet = async (id) => {
  const response = await api.delete(`/question-sets/${id}`);
  return response.data;
};

// ============================================================
// APPOINTMENTS API
// ============================================================

export const fetchAppointments = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.locationId) queryParams.append("locationId", params.locationId);
  if (params.providerId) queryParams.append("providerId", params.providerId);
  if (params.clientId) queryParams.append("clientId", params.clientId);
  if (params.status) queryParams.append("status", params.status);
  // When set, response is { appointments, stats } instead of a plain array —
  // stats are bucket counts computed server-side over the full (uncapped) window.
  if (params.includeStats) queryParams.append("includeStats", "1");

  const url = `/appointments${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;
  const response = await api.get(url);
  return response.data;
};

export const fetchAppointment = async (id) => {
  const response = await api.get(`/appointments/${id}`);
  return response.data;
};

export const createAppointment = async (appointmentData) => {
  const response = await api.post("/appointments", appointmentData);
  return response.data;
};

export const updateAppointment = async (id, appointmentData) => {
  const response = await api.put(`/appointments/${id}`, appointmentData);
  return response.data;
};

export const deleteAppointment = async (id) => {
  const response = await api.delete(`/appointments/${id}`);
  return response.data;
};

// ============================================================
// COMPLIANCE MANAGEMENT API
// ============================================================

const complianceRequest = async (path, options = {}) => {
  const token = localStorage.getItem("token");
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  const endpoint = `${API_URL}${path}`;

  try {
    const response = await fetch(endpoint, {
      method: options.method || "GET",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
      body: options.body,
      signal: controller.signal,
    });

    const raw = await response.text();
    let payload = null;
    try {
      payload = raw ? JSON.parse(raw) : null;
    } catch {
      payload = raw || null;
    }

    if (!response.ok) {
      const err = new Error(
        (payload && payload.message) ||
          (typeof payload === "string" ? payload : "") ||
          `Request failed (${response.status})`,
      );
      err.status = response.status;
      err.payload = payload;
      throw err;
    }

    return payload;
  } catch (error) {
    if (error?.name === "AbortError") {
      const timeoutError = new Error("Request timed out. Please try again.");
      timeoutError.code = "ECONNABORTED";
      throw timeoutError;
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const fetchComplianceFolders = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.locationId) queryParams.append("locationId", params.locationId);
  if (params.parentId) queryParams.append("parentId", params.parentId);
  if (params.parentFolderId) {
    queryParams.append("parentFolderId", params.parentFolderId);
  }

  const url = `/compliance/folders${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;
  return complianceRequest(url);
};

export const fetchComplianceFolder = async (folderId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.locationId) queryParams.append("locationId", params.locationId);
  const qs = queryParams.toString();
  return complianceRequest(
    `/compliance/folders/${encodeURIComponent(folderId)}${qs ? `?${qs}` : ""}`,
  );
};

export const createComplianceFolder = async (folderData) => {
  const response = await api.post("/compliance/folders", folderData);
  return response.data;
};

export const updateComplianceFolder = async (folderId, folderData) => {
  const response = await api.put(`/compliance/folders/${folderId}`, folderData);
  return response.data;
};

export const deleteComplianceFolder = async (folderId) => {
  const response = await api.delete(`/compliance/folders/${folderId}`);
  return response.data;
};

export const renameComplianceFolder = async (folderId, data) => {
  const response = await api.patch(`/compliance/folders/${folderId}`, data);
  return response.data;
};

export const fetchComplianceDocuments = async (folderId) => {
  const url = folderId
    ? `/compliance/documents?folderId=${folderId}`
    : "/compliance/documents";
  const response = await api.get(url);
  return response.data;
};

export const uploadComplianceDocument = async (formData) => {
  const endpoint = `${API_URL}/compliance/documents/upload`;
  const token = localStorage.getItem("token");
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 120000);

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
      signal: controller.signal,
    });

    const raw = await response.text();
    let payload = null;
    try {
      payload = raw ? JSON.parse(raw) : null;
    } catch {
      payload = raw || null;
    }

    if (!response.ok) {
      const error = new Error(
        (payload && payload.message) ||
          (typeof payload === "string" ? payload : "") ||
          `Upload failed (${response.status})`,
      );
      error.status = response.status;
      error.payload = payload;
      throw error;
    }

    return payload;
  } catch (error) {
    const isAbort = error?.name === "AbortError";
    if (isAbort) {
      const timeoutError = new Error(
        "Upload timed out. Please try a smaller file or retry in a moment.",
      );
      timeoutError.code = "ECONNABORTED";
      throw timeoutError;
    }

    console.error("[Compliance Upload] Request failed", {
      endpoint,
      status: error?.status || null,
      code: error?.code || null,
      message: error?.message || "unknown_error",
      detail: error?.payload || null,
    });
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const deleteComplianceDocument = async (documentId) => {
  const response = await api.delete(`/compliance/documents/${documentId}`);
  return response.data;
};

export const fetchExpiringDocuments = async () => {
  const response = await api.get("/compliance/documents/expiring/soon");
  return response.data;
};

// ============================================================
// GEOCODING API
// ============================================================

export const fetchAddressSuggestions = async (query) => {
  const response = await api.get(
    `/geocode/search?q=${encodeURIComponent(query)}`,
  );
  return response.data;
};

// ============================================================
// ANALYTICS API
// ============================================================

export const fetchAnalytics = async (locationId) => {
  const url = locationId ? `/analytics?locationId=${locationId}` : "/analytics";
  const response = await api.get(url);
  return response.data;
};

// ============================================================
// AIP ADVICE / RECOMMENDATIONS API
// ============================================================

export const saveAdvice = async (adviceData) => {
  const response = await api.post("/advice", adviceData);
  return response.data;
};

export const fetchAdviceForClient = async (clientId) => {
  const response = await api.get(`/advice/client/${clientId}`);
  return response.data;
};

export const fetchLatestAdviceForClient = async (clientId) => {
  try {
    const response = await api.get(`/advice/client/${clientId}/latest`);
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) {
      // No advice yet for this client; treat as empty instead of breaking UI.
      return null;
    }
    throw error;
  }
};

export const fetchAllAdvice = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const response = await api.get(
    `/advice${queryString ? `?${queryString}` : ""}`,
  );
  return response.data;
};

export const updateAdviceStatus = async (adviceId, status) => {
  const response = await api.put(`/advice/${adviceId}/status`, { status });
  return response.data;
};

export const deleteAdvice = async (adviceId) => {
  const response = await api.delete(`/advice/${adviceId}`);
  return response.data;
};

// ============================================================
// ABLE CARE API (Grip Strength / Gait Speed)
// ============================================================

export const fetchAbleCareMeasurements = async (clientId) => {
  try {
    const response = await api.get(`/able-care/clients/${clientId}/measurements`);
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404 || error?.response?.status === 400) {
      return { measurements: [], latestByType: {} };
    }
    throw error;
  }
};

export const enrolClientAbleCare = async (clientId, options = {}) => {
  const response = await api.post(`/able-care/clients/${clientId}/enrol`, options);
  return response.data;
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Generic error handler for API calls
 * Can be used in components for consistent error handling
 */
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    console.error("API Error:", error.response.status, error.response.data);
    return error.response.data?.message || "An error occurred";
  } else if (error.request) {
    // Request was made but no response
    console.error("Network Error:", error.request);
    return "Network error - please check your connection";
  } else {
    // Something else happened
    console.error("Error:", error.message);
    return error.message;
  }
};

/**
 * ============================================================
 * MIGRATION NOTES
 * ============================================================
 * This file has been updated to use the new api client with:
 * - Auto-attached auth headers (via request interceptor)
 * - Auto-logout on 401 (via response interceptor)
 * - Consistent error handling
 * - No manual token management needed
 *
 * All API calls now go through the shared axios instance.
 * ============================================================
 */
